import jsPDF from "jspdf";
import { format, eachDayOfInterval } from "date-fns";
import { es } from "date-fns/locale";
import { Sale } from "../../../types/common.types";

interface GenerateWeeklySummaryParams {
    sellerName: string
    day: Date
    endDay?: Date
    sales: Sale[]
    commissionTotal: number   // pagado + pendiente
    daysWorkedCount: number
    daysWorkedAmount: number
    desfasesTotal?: number
    advancesTotal?: number
    total: number             // el mismo monto del pago pendiente (totalAPagar)
}

// Mismo formato con el que David hoy le manda el detalle de comisiones a cada vendedor por
// WhatsApp a mano — esto lo arma solo al crear el pago pendiente en Caja, mismo estilo visual del
// voucher (logo, fuentes Roboto) para que se sienta parte de la misma familia de documentos.
export default function generateWeeklySummaryPDF({
    sellerName, day, endDay, sales, commissionTotal, daysWorkedCount, daysWorkedAmount, desfasesTotal, advancesTotal, total
}: GenerateWeeklySummaryParams) {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const marginX = 20

    try {
        doc.addFont('/fonts/Roboto-Regular.ttf', 'Roboto', 'normal')
        doc.addFont('/fonts/Roboto-Medium.ttf', 'Roboto', 'medium')
        doc.addFont('/fonts/Roboto-Bold.ttf', 'Roboto', 'bold')
    } catch (error) {
        console.warn('Font loading failed, using default fonts:', error)
        doc.setFont('helvetica')
    }

    try {
        const imagePadding = 2
        const imageSize = 30
        doc.addImage('/assets/logo.png', 'PNG', 14 + imagePadding, 5 + imagePadding, imageSize - (imagePadding * 2), imageSize - (imagePadding * 2))
    } catch (error) {
        console.log('Could not load logo:', error)
    }

    let y = 15

    function checkPageBreak(requiredSpace = 15) {
        if (y + requiredSpace > pageHeight - 20) {
            doc.addPage()
            y = 20
        }
    }

    doc.setFont('Roboto', 'bold')
    doc.setFontSize(18)
    doc.text("MOLANTUR", pageWidth / 2, y, { align: "center" })
    y += 8
    doc.setFontSize(14)
    doc.text("ATACAMA TOURS", pageWidth / 2, y, { align: "center" })
    y += 15

    doc.setFontSize(15)
    doc.text("Resumen de la Semana", pageWidth / 2, y, { align: "center" })
    y += 7
    doc.text(`de ${sellerName}`, pageWidth / 2, y, { align: "center" })
    y += 7
    doc.setFont('Roboto', 'normal')
    doc.setFontSize(11)
    const rangeLabel = endDay
        ? `Del día ${format(day, 'd')} al ${format(endDay, 'd')} de ${format(endDay, 'MMMM yyyy', { locale: es })}`
        : `Día ${format(day, "d 'de' MMMM yyyy", { locale: es })}`
    doc.text(rangeLabel, pageWidth / 2, y, { align: "center" })
    y += 12

    // Un renglón por día del rango — los días sin ventas igual aparecen, solo que sin nada debajo,
    // para que quede claro que ese día no hubo comisiones (no que se nos olvidó anotarlo).
    const days = eachDayOfInterval({ start: day, end: endDay || day })
    for (const d of days) {
        checkPageBreak(14)
        doc.setFont('Roboto', 'medium')
        doc.setFontSize(11)
        doc.text(`Día ${format(d, 'd')}`, marginX, y)
        y += 6

        const dayKey = format(d, 'yyyy-MM-dd')
        // healthByDay (backend) devuelve la fecha del tour en reservation.trip.trip_date, no en
        // reservation.reservation_date — y algunas reservas viejas pueden traer el trip incompleto,
        // así que se valida antes de formatear en vez de reventar con "Invalid time value".
        const daySales = sales.filter(s => {
            const rawDate = s.reservation?.trip?.trip_date
            if (!rawDate) return false
            const parsed = new Date(rawDate)
            if (isNaN(parsed.getTime())) return false
            return format(parsed, 'yyyy-MM-dd') === dayKey
        })

        // Agrupa por (tour, % comisión) — así "6 pasajeros de Purilibre al 7%" queda en una sola
        // línea en vez de 6 líneas repetidas.
        const groups: Record<string, { tour_id: string, percent: number, count: number }> = {}
        for (const sale of daySales) {
            // sale.amount != null (no ?) — un monto de comisión corregido a $0 es un valor real,
            // no "no se fijó un monto"; solo cae al % por defecto del vendedor cuando no vino nada.
            const hasAmountOverride = sale.amount !== null && sale.amount !== undefined
            const percent = Math.round(hasAmountOverride ? (sale.amount * 100) / sale.reservation.price : sale.seller.sell_commission)
            const key = `${sale.reservation.tour_id}|${percent}`
            if (!groups[key]) groups[key] = { tour_id: sale.reservation.tour_id, percent, count: 0 }
            groups[key].count++
        }

        doc.setFont('Roboto', 'normal')
        doc.setFontSize(10)
        for (const g of Object.values(groups)) {
            checkPageBreak(6)
            doc.text(`${g.count} ${g.tour_id} al ${g.percent}%`, marginX + 4, y)
            y += 5.5
        }
        y += 2
    }

    y += 4
    checkPageBreak(30)
    doc.setDrawColor(200)
    doc.line(marginX, y, pageWidth - marginX, y)
    y += 8

    doc.setFont('Roboto', 'bold')
    doc.setFontSize(12)
    doc.text(`Comisiones $ ${commissionTotal.toLocaleString('es-CL')}`, marginX, y)
    y += 7
    doc.text(`Días trabajados ${daysWorkedCount} - $ ${daysWorkedAmount.toLocaleString('es-CL')}`, marginX, y)
    y += 7
    if (desfasesTotal) {
        doc.text(`Desfases $ ${desfasesTotal.toLocaleString('es-CL')}`, marginX, y)
        y += 7
    }
    if (advancesTotal) {
        doc.text(`Anticipos -$ ${advancesTotal.toLocaleString('es-CL')}`, marginX, y)
        y += 7
    }
    y += 3
    doc.setFontSize(14)
    doc.text(`Total $ ${total.toLocaleString('es-CL')}`, marginX, y)

    const fileName = `resumen_semana_${sellerName.replace(/\s+/g, '_')}_${format(day, 'yyyyMMdd')}.pdf`
    doc.save(fileName)
}
